<template>
  <div class="metrics-writing-breakdown">
    <div class="metrics-writing-breakdown__header">
      <div>
        <h3 class="metrics-writing-breakdown__title heading-s">
          {{ $t("metrics.assignmentWritingBreakdown") }}
        </h3>
        <p class="metrics-writing-breakdown__subtitle caption-s">
          {{ $t("metrics.aiDetectedPatternsSubtitle") }}
        </p>
      </div>
    </div>

    <infinity-loading v-if="isLoading && !hasData" />

    <div v-else-if="hasData" class="metrics-writing-breakdown__grid">
      <div
        class="metrics-writing-breakdown__col-header metrics-writing-breakdown__col-header--strengths"
      >
        <eg-icon
          icon="check-circle"
          class="metrics-writing-breakdown__col-icon"
        />
        <span>{{ $t("metrics.strengths") }}</span>
      </div>
      <div
        class="metrics-writing-breakdown__col-header metrics-writing-breakdown__col-header--needs-work"
      >
        <eg-icon
          icon="exclamation-triangle"
          class="metrics-writing-breakdown__col-icon"
        />
        <span>{{ $t("metrics.needsWork") }}</span>
      </div>

      <template v-for="(_, index) in maxItems">
        <div
          v-if="breakdown.strengths?.[index]"
          :key="`s-${index}`"
          class="metrics-writing-breakdown__card"
        >
          <div
            class="metrics-writing-breakdown__card-icon-wrap metrics-writing-breakdown__card-icon-wrap--strengths"
          >
            <span
              class="metrics-writing-breakdown__card-svg"
              :style="{
                maskImage: `url(${
                  strengthsIcons[index % strengthsIcons.length]
                })`,
                WebkitMaskImage: `url(${
                  strengthsIcons[index % strengthsIcons.length]
                })`,
              }"
            />
          </div>
          <div class="metrics-writing-breakdown__card-body">
            <h4 class="metrics-writing-breakdown__card-title">
              {{ breakdown.strengths[index].title }}
            </h4>
            <p class="metrics-writing-breakdown__card-desc">
              {{ breakdown.strengths[index].description }}
            </p>
            <span class="metrics-writing-breakdown__card-meta">
              {{ breakdown.strengths[index].percentage
              }}{{ $t("metrics.percentOfStudents") }}
            </span>
          </div>
        </div>
        <div
          v-else
          :key="`s-empty-${index}`"
          class="metrics-writing-breakdown__card-placeholder"
        />

        <div
          v-if="breakdown.needsImprovement?.[index]"
          :key="`n-${index}`"
          class="metrics-writing-breakdown__card"
        >
          <div
            class="metrics-writing-breakdown__card-icon-wrap metrics-writing-breakdown__card-icon-wrap--needs-work"
          >
            <span
              class="metrics-writing-breakdown__card-svg"
              :style="{
                maskImage: `url(${
                  needsWorkIcons[index % needsWorkIcons.length]
                })`,
                WebkitMaskImage: `url(${
                  needsWorkIcons[index % needsWorkIcons.length]
                })`,
              }"
            />
          </div>
          <div class="metrics-writing-breakdown__card-body">
            <h4 class="metrics-writing-breakdown__card-title">
              {{ breakdown.needsImprovement[index].title }}
            </h4>
            <p class="metrics-writing-breakdown__card-desc">
              {{ breakdown.needsImprovement[index].description }}
            </p>
            <span class="metrics-writing-breakdown__card-meta">
              {{ breakdown.needsImprovement[index].percentage
              }}{{ $t("metrics.percentOfStudents") }}
            </span>
          </div>
        </div>
        <div
          v-else
          :key="`n-empty-${index}`"
          class="metrics-writing-breakdown__card-placeholder"
        />
      </template>
    </div>

    <metrics-empty-state v-else-if="!isLoading" />
  </div>
</template>

<script>
import MetricsEmptyState from "./metrics_empty_state.vue";
import InfinityLoading from "@/components/rubrics/newRubric/components/infinity-loading.vue";

const STRENGTHS_ICONS = [
  require("@/assets/eg-icons/uF16C-clear-central-thesis.svg"),
  require("@/assets/eg-icons/uF16D-use-of-statistics.svg"),
  require("@/assets/eg-icons/uF16E-formal-academic-tone.svg"),
];

const NEEDS_WORK_ICONS = [
  require("@/assets/eg-icons/uF16F-weak-paragraph-transitions.svg"),
  require("@/assets/eg-icons/uF188-addressing-counterarguments.svg"),
  require("@/assets/eg-icons/uF189-conclusion-strength.svg"),
];

export default {
  name: "MetricsWritingBreakdown",

  components: { MetricsEmptyState, InfinityLoading },

  props: {
    breakdown: {
      type: Object,
      default: null,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      strengthsIcons: STRENGTHS_ICONS,
      needsWorkIcons: NEEDS_WORK_ICONS,
    };
  },

  computed: {
    hasData() {
      return (
        this.breakdown &&
        (this.breakdown.strengths?.length > 0 ||
          this.breakdown.needsImprovement?.length > 0)
      );
    },
    maxItems() {
      return Math.max(
        this.breakdown?.strengths?.length ?? 0,
        this.breakdown?.needsImprovement?.length ?? 0
      );
    },
  },
};
</script>

<style lang="scss" scoped>
@import "metrics_card";

.metrics-writing-breakdown {
  @include metrics-card;
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin: 0 24px;

  &__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
  }

  &__title {
    font-size: var(--typography-font-size-fs-400);
    font-weight: var(--typography-font-weight-fw-700, 700);
    color: var(--color-text-neutral-primary, #1a2330);
    margin: 0 0 4px;
  }

  &__subtitle {
    color: var(--color-text-neutral-fade);
    margin: 0;
  }

  &__grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    column-gap: 24px;
    row-gap: 12px;

    @media (max-width: 900px) {
      grid-template-columns: 1fr;

      .metrics-writing-breakdown__col-header--strengths {
        order: 1;
      }

      > *:nth-child(2n + 3) {
        order: 2;
      }

      .metrics-writing-breakdown__col-header--needs-work {
        order: 3;
      }

      > *:nth-child(2n + 4) {
        order: 4;
      }
    }
  }

  &__col-header {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: var(--typography-font-size-fs-200, 12px);
    font-weight: var(--typography-font-weight-fw-700, 700);
    letter-spacing: 0.06em;
    text-transform: uppercase;

    &--strengths {
      color: var(--color-text-semantic-onSuccess, #308c4a);
    }

    &--needs-work {
      color: var(--color-text-semantic-onWarning, #d76812);
    }
  }

  &__col-icon {
    font-size: 14px;
  }

  &__card {
    display: flex;
    align-items: stretch;
    gap: 12px;
    padding: 14px;
    flex: 1;
    border: 1px solid var(--color-stroke-neutral-default, #e5ebf0);
    border-radius: var(--radius-rd-300, 8px);
    background: var(--color-surface-neutral-container-1, #f6f8fa);
  }

  &__card-svg {
    display: block;
    width: 16px;
    height: 16px;
    background-color: currentColor;
    mask-size: contain;
    mask-repeat: no-repeat;
    mask-position: center;
    -webkit-mask-size: contain;
    -webkit-mask-repeat: no-repeat;
    -webkit-mask-position: center;
  }

  &__card-icon-wrap {
    display: flex;
    align-items: center;
    justify-content: center;
    align-self: flex-start;
    width: 34px;
    height: 34px;
    border-radius: 8px;
    flex-shrink: 0;

    &--strengths {
      background: var(--color-surface-neutral-white, #fff);
      color: var(--color-text-semantic-onSuccess, #308c4a);
    }

    &--needs-work {
      background: var(--color-surface-neutral-white, #fff);
      color: var(--color-text-semantic-onWarning, #d76812);
    }
  }

  &__card-body {
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex: 1;
    min-width: 0;
  }

  &__card-title {
    font-size: var(--typography-font-size-fs-300, 14px);
    font-weight: var(--typography-font-weight-fw-700, 700);
    color: var(--color-text-neutral-primary, #1a2330);
    margin: 0;
    line-height: 1.3;
  }

  &__card-desc {
    font-size: var(--typography-font-size-fs-300);
    font-weight: var(--typography-font-weight-fw-400);
    line-height: 140%;
    letter-spacing: 0;
    color: var(--color-text-neutral-secondary, #566370);
    margin: 0;
  }

  &__card-meta {
    font-size: var(--typography-font-size-fs-200);
    font-weight: var(--typography-font-weight-fw-600);
    line-height: 120%;
    letter-spacing: 0;
    color: var(--color-text-neutral-fade);
    margin-top: auto;
    padding-top: 4px;
  }
}
</style>
